import React, { useEffect, useRef, useState } from "react";

interface Props {
  data: any[];
  configs: any[];
  onVisibleRowsChange?: (visible: any[]) => void;
}

const CommonTable = ({ data, configs, onVisibleRowsChange }: Props) => {
  const lastVisibleIds = useRef<string[]>([]);
  const rowsPerPage = 10; // Número de filas por página
  const [currentPage, setCurrentPage] = useState(1); // Página actual
  const tableRef = useRef<HTMLDivElement>(null);
  const [tableHeight, setTableHeight] = useState(0);
  
  const prevDataLength = useRef(data.length);

  useEffect(() => {
    // Solo resetear si el tamaño del array cambió (se agregó/eliminó elementos)
    if (data.length !== prevDataLength.current) {
      const maxPage = Math.ceil(data.length / rowsPerPage) || 1;
      
      // Solo ajustar si la página actual ya no existe
      if (currentPage > maxPage) {
        setCurrentPage(maxPage);
      }
      
      prevDataLength.current = data.length;
    }
  }, [data.length, currentPage, rowsPerPage]);

  // Calcular altura dinámica de la tabla
  useEffect(() => {
    if (tableRef.current) {
      setTableHeight(tableRef.current.offsetHeight);
    }
  }, [data, currentPage]);

  // Calcular el índice de inicio y fin de las filas para la página actual
  const startIndex = (currentPage - 1) * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  const currentData = data.slice(startIndex, endIndex); // Filas de la página actual

  useEffect(() => {
    const currentIds = currentData.map((item) => item.id).join(",");
    const lastIds = lastVisibleIds.current.join(",");

    if (currentIds !== lastIds && onVisibleRowsChange) {
      onVisibleRowsChange(currentData);
      lastVisibleIds.current = currentData.map((item) => item.id);
    }
  }, [currentData]);

  // Calcular el número total de páginas
  const totalPages = Math.ceil(data.length / rowsPerPage);

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage((prev) => prev + 1);
    }
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage((prev) => prev - 1);
    }
  };

  const renderedRows = currentData.map((company: any) => (
    <tr key={company.id}>
      {configs.map((val: any, idx: number) => (
        <td key={idx} className="p-4 whitespace-nowrap text-sm font-normal text-gray-900 hover:bg-gray-50">
          {val.render(company)}
        </td>
      ))}
    </tr>
  ));

  const renderedHeaders = configs.map((config: any) => (
    <th
      key={config.label}
      scope="col"
      className="p-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap"
    >
      {config.label}
    </th>
  ));

  return (
    <div className="bg-white shadow rounded-lg p-4 sm:p-6 xl:p-8 relative">
      {/* Contenedor que escapa del overflow-x-hidden */}
      <div
        className="absolute left-0 right-0 overflow-x-auto scrollbar-custom p-1"
        ref={tableRef}
      >
      <table className="table-auto w-full divide-y divide-gray-200">
        <thead>
          <tr>{renderedHeaders}</tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {renderedRows}
        </tbody>
      </table>
      </div>
      {/* Espacio reservado dinámico para la tabla */}
      <div style={{ height: tableHeight }}></div>

      {/* Botones debajo de la tabla */}
      <div className="flex justify-between items-center mt-4">
        <button
          onClick={handlePreviousPage}
          disabled={currentPage === 1}
          className={`px-4 py-2 text-sm font-medium ${currentPage === 1 ? 'text-gray-400' : 'text-blue-500 hover:text-blue-700'}`}
        >
          Previous
        </button>
        <span className="text-sm text-gray-700">
          Page {currentPage} of {totalPages}
        </span>
        <button
          onClick={handleNextPage}
          disabled={currentPage === totalPages}
          className={`px-4 py-2 text-sm font-medium ${currentPage === totalPages ? 'text-gray-400' : 'text-blue-500 hover:text-blue-700'}`}
        >
          Next
        </button>
      </div>
    </div>
  );
};

export default CommonTable;